/* =============================================================================
   KNN Free Gift — shared engine for the managed Free Gift programme.
   Migrated logic. Same architecture, same cart
   properties, same source::parentVariantId pairing; the host theme differs, so the
   cart calls are Horizon/KNN's key-based Cart AJAX API instead of the KT globals
   (Shopify.KT_removeItem / Shopify.KT_changeItemSubs no longer exist here).

   Two flows, declared per product in snippets/free-gift-rules.liquid:

     old_funnel_subscription_gift  (Funnel 4)
       Only the Subscribe & Save 3 Bottles variant qualifies. The parent is added
       WITH its selling plan; the gift is added as a separate line with NO selling
       plan, so Recharge ships it once with the first order and never renews it.

     otp_gift  (Supergreens Funnel 6)
       One-time bundles earn gifts: 3 Bottles -> Frother, 6 Bottles -> Frother +
       Liver Support. Any number of gifts per parent via the rule's `gifts` array.

   Cart line properties (all underscore-prefixed, so Shopify keeps them out of the
   customer-facing property list and off the order confirmation):

     parent line   _free_gift_parent, _free_gift_source, _free_gift_rule,
                   _free_gift_variant_id, _free_gift_variant_ids
     gift line     _free_gift, _gift_source, _gift_parent_variant_id,
                   _gift_qty_per_parent, _gift_rule, _gift_label,
                   _gift_original_price_cents

   Consumers: blocks/knn-product-offer.liquid (add to cart), assets/knn-mini-cart.js
   and assets/knn-cart.js (removal, orphan cleanup, subscribe upgrade).
   ============================================================================= */
(function () {
  'use strict';

  /* Every gift source the theme manages. A source missing from this list is not
     detected as a gift, not paired to its parent, not cleaned up and not rendered
     with gift styling — so a newly migrated Funnel 4 product MUST be added here at
     the same time as its row in snippets/free-gift-rules.liquid. */
  var MANAGED_FREE_GIFT_SOURCES = [
    'f4_essentials',
    'nitric_oxide_funnel_6',
    'supergreens_funnel_6',
    'testo_booster_funnel_4_subscription_gift',
    'liver_support_funnel_4_subscription_gift',
    'hair_complex_funnel_4_subscription_gift',
    'super_fruits_veggies_funnel_4_subscription_gift',
    'supergreens_funnel_4_subscription_gift',
    'omega_3_funnel_4_subscription_gift',
    'gut_health_funnel_4_subscription_gift',
    'mushroom_complex_funnel_4_subscription_gift',
    'nootropic_funnel_4_subscription_gift',
    'turmeric_curcumin_funnel_4_subscription_gift'
  ];

  /* ---------------------------------------------------------------------------
     Config
     --------------------------------------------------------------------------- */

  function parseFreeGiftConfig(raw) {
    if (!raw) return null;
    try {
      return JSON.parse(raw);
    } catch (error) {
      console.error('[knn-free-gift] invalid config JSON:', error);
      return null;
    }
  }

  /* The PDP config, emitted by snippets/free-gift-config.liquid. Absent on every
     product without a gift programme, and on every non-product page. */
  function getFreeGiftConfig() {
    var el = document.getElementById('free-gift-config');
    return el ? parseFreeGiftConfig(el.textContent) : null;
  }

  function isUsableConfig(config, flow) {
    if (!config || !config.enabled) return false;
    if (!isManagedFreeGiftSource(config.source)) return false;
    if (flow && config.flow !== flow) return false;
    return true;
  }

  function getFreeGiftRuleForVariant(config, variantId) {
    if (!config || !Array.isArray(config.rules)) return null;
    var target = String(variantId);
    for (var i = 0; i < config.rules.length; i++) {
      if (String(config.rules[i].parent_variant_id) === target) return config.rules[i];
    }
    return null;
  }

  function hasFreeGiftValue(value) {
    return value !== undefined && value !== null && String(value) !== '';
  }

  /* Normalises both rule shapes to one list: the multi-gift `gifts` array, or the
     single flat gift_variant_id/gift_qty_per_parent form. */
  function getFreeGiftsFromRule(rule) {
    if (!rule) return [];

    if (Array.isArray(rule.gifts) && rule.gifts.length) {
      return rule.gifts
        .map(function (gift) {
          return {
            gift_variant_id: gift.gift_variant_id,
            gift_qty: gift.gift_qty || gift.gift_qty_per_parent || 1,
            gift_label: gift.gift_label || '',
            once_per_order: gift.once_per_order === true,
            gift_original_price_cents: gift.gift_original_price_cents
          };
        })
        .filter(function (gift) {
          return gift.gift_variant_id;
        });
    }

    if (rule.gift_variant_id) {
      return [
        {
          gift_variant_id: rule.gift_variant_id,
          gift_qty: rule.gift_qty_per_parent || 1,
          gift_label: rule.gift_label || '',
          gift_original_price_cents: rule.gift_original_price_cents
        }
      ];
    }

    return [];
  }

  /* ---------------------------------------------------------------------------
     Cart item classification
     --------------------------------------------------------------------------- */

  function isManagedFreeGiftSource(source) {
    return MANAGED_FREE_GIFT_SOURCES.indexOf(String(source || '').trim()) !== -1;
  }

  function itemProperties(item) {
    return (item && item.properties) || {};
  }

  function getItemFreeGiftSource(item) {
    var props = itemProperties(item);
    return String(props._gift_source || props._free_gift_source || '').trim();
  }

  function isFreeGiftCartItem(item) {
    return String(itemProperties(item)._free_gift || '') === 'true';
  }

  function isManagedFreeGiftItem(item) {
    return isFreeGiftCartItem(item) && isManagedFreeGiftSource(getItemFreeGiftSource(item));
  }

  /* These are catalog-only reward products. They may enter a cart only with the
     private parent/source properties written by one of the managed gift flows. */
  function isRestrictedGiftProduct(item) {
    var title = String((item && (item.product_title || item.title)) || '').toLowerCase();
    var handle = String((item && item.handle) || '').toLowerCase();
    return /free\s*gift/.test(title) || /free\s+shipping/.test(title) || handle === 'free-shipping' || /e[\s-]?book/.test(title) || /e[\s-]?book/.test(handle);
  }

  function isManagedFreeGiftParentItem(item) {
    return !isFreeGiftCartItem(item) && isManagedFreeGiftSource(getItemFreeGiftSource(item));
  }

  /* Gifts belong to one parent VARIANT within one gift SOURCE. Pairing on both means
     two funnels, or two products in the same funnel, can never remove each other's
     gifts. */
  function buildFreeGiftPairKey(source, variantId) {
    return String(source || '').trim() + '::' + String(variantId || '').trim();
  }

  function itemParentPairKey(item) {
    return buildFreeGiftPairKey(getItemFreeGiftSource(item), itemProperties(item)._gift_parent_variant_id || '');
  }

  function findExistingGiftPair(cart, source, parentVariantId) {
    var sourceStr = String(source || '').trim();
    var parentVariantIdStr = String(parentVariantId || '');
    var parentItem = null;
    var giftItems = [];

    ((cart && cart.items) || []).forEach(function (item) {
      if (getItemFreeGiftSource(item) !== sourceStr) return;

      if (!isFreeGiftCartItem(item) && String(item.variant_id) === parentVariantIdStr) {
        parentItem = item;
      }

      if (isFreeGiftCartItem(item) && String(itemProperties(item)._gift_parent_variant_id || '') === parentVariantIdStr) {
        giftItems.push(item);
      }
    });

    return { parentItem: parentItem, giftItem: giftItems[0] || null, giftItems: giftItems };
  }

  /* ---------------------------------------------------------------------------
     Item building
     --------------------------------------------------------------------------- */

  function buildParentProperties(config, rule, gifts, baseProperties) {
    var props = Object.assign({}, baseProperties || {});
    props._free_gift_parent = 'true';
    props._free_gift_source = config.source;
    props._free_gift_rule = rule.gift_rule || '';
    props._free_gift_variant_id = String(gifts[0].gift_variant_id);
    props._free_gift_variant_ids = gifts
      .map(function (gift) {
        return String(gift.gift_variant_id);
      })
      .join(',');
    return props;
  }

  function buildGiftItem(config, rule, gift, parentVariantId) {
    var props = {
      _free_gift: 'true',
      _gift_source: config.source,
      _gift_parent_variant_id: String(parentVariantId),
      _gift_qty_per_parent: String(gift.gift_qty || 1),
      _gift_rule: rule.gift_rule || '',
      _gift_label: gift.gift_label || ''
    };
    if (config.source === 'f4_essentials' && gift.once_per_order) props._gift_once_per_order = 'true';

    if (hasFreeGiftValue(gift.gift_original_price_cents)) {
      props._gift_original_price_cents = String(gift.gift_original_price_cents);
    }

    return {
      id: Number(gift.gift_variant_id),
      quantity: parseInt(gift.gift_qty || '1', 10) || 1,
      properties: props
    };
  }

  /* Funnel 4. Returns [gift..., parent] — the gift first so it reads above its parent
     in the cart, matching past logic. Falls back to [parent] untouched when the
     variant does not qualify, so OTP tiers and SnS 1/2 Bottles add normally. */
  function buildOldFunnelSubscriptionGiftItems(options) {
    var opts = options || {};
    var config = opts.config || getFreeGiftConfig();
    var quantity = parseInt(opts.quantity, 10) || 1;

    var mainItem = { id: Number(opts.variantId), quantity: quantity };
    if (opts.properties && Object.keys(opts.properties).length) {
      mainItem.properties = Object.assign({}, opts.properties);
    }
    /* The parent carries the plan; the gift deliberately does not, so it is a
       one-time line that never joins the Recharge recurring shipment. */
    if (opts.sellingPlanId) {
      mainItem.selling_plan = Number(opts.sellingPlanId);
    }

    if (!isUsableConfig(config, 'old_funnel_subscription_gift')) return [mainItem];
    if (config.source === 'f4_essentials' && !opts.sellingPlanId) return [mainItem];

    var rule = getFreeGiftRuleForVariant(config, opts.variantId);
    var gifts = getFreeGiftsFromRule(rule);
    if (!rule || !gifts.length) return [mainItem];

    mainItem.properties = buildParentProperties(config, rule, gifts, mainItem.properties);

    var giftItems = gifts.map(function (gift) {
      return buildGiftItem(config, rule, gift, opts.variantId);
    });

    return giftItems.concat([mainItem]);
  }

  /* Supergreens Funnel 6. Cart-aware: caps the 6_plus_2 parent at 2 and never adds a
     gift the parent already has. Returns null when the variant has no rule, which is
     the signal to let the theme's normal add to cart run untouched. */
  function buildOtpGiftItems(cart, options) {
    var opts = options || {};
    var config = opts.config || getFreeGiftConfig();
    if (!isUsableConfig(config, 'otp_gift')) return null;

    var rule = getFreeGiftRuleForVariant(config, opts.variantId);
    var gifts = getFreeGiftsFromRule(rule);
    if (!rule || !gifts.length) return null;

    var pair = findExistingGiftPair(cart, config.source, opts.variantId);
    var quantityToAdd = parseInt(opts.quantity, 10) || 1;

    /* Managed 6-bottle bundles are capped at 2 in the cart, as in past store. */
    if (rule.gift_rule === '6_plus_2') {
      var currentQty = parseInt((pair.parentItem && pair.parentItem.quantity) || '0', 10) || 0;
      var remainingQty = 2 - currentQty;
      if (remainingQty <= 0) return { items: [], maxReached: true };
      quantityToAdd = Math.min(quantityToAdd, remainingQty);
    }

    var mainItem = {
      id: Number(opts.variantId),
      quantity: quantityToAdd,
      properties: buildParentProperties(config, rule, gifts, opts.properties)
    };
    if (opts.sellingPlanId) {
      mainItem.selling_plan = Number(opts.sellingPlanId);
    }

    var existingGiftVariantIds = pair.giftItems.map(function (item) {
      return String(item.variant_id);
    });

    var items = gifts
      .filter(function (gift) {
        return existingGiftVariantIds.indexOf(String(gift.gift_variant_id)) === -1;
      })
      .map(function (gift) {
        return buildGiftItem(config, rule, gift, opts.variantId);
      });

    items.push(mainItem);

    return { items: items, maxReached: false };
  }

  /* Drops any gift line the cart already holds for the same source + parent + gift
     variant, so re-adding a qualifying product never stacks duplicate gifts. */
  function dropDuplicateGiftItems(cart, items) {
    var cartItems = (cart && cart.items) || [];
    var sharedAdded = {};

    return (items || []).filter(function (item) {
      var props = item.properties || {};
      if (String(props._free_gift || '') !== 'true') return true;
      if (props._gift_source === 'f4_essentials' && props._gift_once_per_order === 'true') {
        var id = String(item.id);
        if (sharedAdded[id]) return false;
        sharedAdded[id] = true;
        return !cartItems.some(function (line) {
          return String(line.variant_id) === id && isFreeGiftCartItem(line) && getItemFreeGiftSource(line) === 'f4_essentials';
        });
      }

      var alreadyPresent = cartItems.some(function (cartItem) {
        return (
          String(cartItem.variant_id) === String(item.id) &&
          isFreeGiftCartItem(cartItem) &&
          getItemFreeGiftSource(cartItem) === String(props._gift_source || '') &&
          String(itemProperties(cartItem)._gift_parent_variant_id || '') ===
            String(props._gift_parent_variant_id || '')
        );
      });

      return !alreadyPresent;
    });
  }

  /* ---------------------------------------------------------------------------
     Cart mutations
     --------------------------------------------------------------------------- */

  function fetchCartJson() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) {
      if (!r.ok) throw new Error('Unable to fetch cart.js');
      return r.json();
    });
  }

  function changeLine(body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) {
      if (!r.ok) {
        return r.text().then(function (text) {
          throw new Error(text || 'cart/change.js failed');
        });
      }
      return r.json();
    });
  }

  /* Submit the parent and its gifts together, addressed by cart line key, so
     an interrupted sequence cannot remove the parent before requesting its gifts. */
  function removeCartLinesTogether(lineKeys) {
    var keys = (lineKeys || []).filter(function (key, index, all) {
      return key && all.indexOf(key) === index;
    });

    if (!keys.length) return Promise.resolve();
    var updates = {};
    keys.forEach(function (key) { updates[key] = 0; });
    return fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ updates: updates })
    }).then(function (r) {
      if (!r.ok) throw new Error('Unable to remove product and gifts');
      return r.json();
    });
  }

  /* Every line that must go when the customer removes `lineKey`: the line itself plus
     each managed gift paired to it. Returns null when the line is not a managed
     parent, meaning the caller should just do its normal removal.

     A gift is kept if ANOTHER line in the cart still qualifies as its parent (same
     source + parent variant) — so removing one of two identical parent lines does not
     strand the survivor without its gift. */
  function sharedGiftHasParent(items, gift, excludedKey) {
    return items.some(function (parent) {
      if (parent.key === excludedKey || getItemFreeGiftSource(parent) !== 'f4_essentials' || !isManagedFreeGiftParentItem(parent) || !parent.selling_plan_allocation) return false;
      var ids = String(itemProperties(parent)._free_gift_variant_ids || '').split(',');
      return ids.indexOf(String(gift.variant_id)) !== -1;
    });
  }

  function collectLinesForParentRemoval(cart, lineKey) {
    var items = (cart && cart.items) || [];
    var target = null;

    items.forEach(function (item) {
      if (item.key === lineKey) target = item;
    });

    if (!target || !isManagedFreeGiftParentItem(target)) return null;

    var pairKey = buildFreeGiftPairKey(getItemFreeGiftSource(target), target.variant_id);

    var otherParentSurvives = items.some(function (item) {
      return (
        item.key !== lineKey &&
        isManagedFreeGiftParentItem(item) &&
        buildFreeGiftPairKey(getItemFreeGiftSource(item), item.variant_id) === pairKey
      );
    });

    var keys = [target.key];
    if (otherParentSurvives) return keys;

    items.forEach(function (item) {
      if (!isManagedFreeGiftItem(item)) return;
      if (getItemFreeGiftSource(item) === 'f4_essentials' && itemProperties(item)._gift_once_per_order === 'true') {
        if (!sharedGiftHasParent(items, item, lineKey)) keys.push(item.key);
        return;
      }
      if (itemParentPairKey(item) === pairKey) keys.push(item.key);
    });

    return keys;
  }

  function initRestrictedGiftProductControls() {
    if (document.body && document.body.dataset.knnRestrictedGiftProduct === 'true') {
      document.querySelectorAll(
        '[data-knn-offer-atc], [data-knn-card-atc], .product-form-buttons, sticky-add-to-cart, .shopify-payment-button'
      ).forEach(function (control) {
        control.hidden = true;
        control.querySelectorAll('button').forEach(function (button) { button.disabled = true; });
      });
    }

    document.addEventListener('click', function (event) {
      var target = event.target instanceof Element ? event.target : null;
      if (!target || !document.body || document.body.dataset.knnRestrictedGiftProduct !== 'true') return;
      if (!target.closest(
        '[data-knn-offer-atc], [data-knn-card-atc], .add-to-cart-button, button[name="add"], .shopify-payment-button'
      )) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);

    document.addEventListener('submit', function (event) {
      var form = event.target;
      var restrictedProductSubmit = form instanceof HTMLFormElement &&
        document.body && document.body.dataset.knnRestrictedGiftProduct === 'true' && /\/cart\/add/.test(form.action);
      if (!restrictedProductSubmit) return;
      event.preventDefault();
      event.stopImmediatePropagation();
    }, true);
  }

  /* Supergreens Funnel 6 OTP -> Subscribe & Save: the customer already owns the gifts,
     so instead of re-adding them we point them at the new subscription parent. Without
     this the gifts would be orphaned by the next cleanup pass. */
  function reassignGiftParents(cart, source, oldParentVariantId, newParentVariantId) {
    var sourceStr = String(source || '').trim();
    var oldId = String(oldParentVariantId || '');
    var newId = String(newParentVariantId || '');

    var targets = ((cart && cart.items) || []).filter(function (item) {
      return (
        isManagedFreeGiftItem(item) &&
        getItemFreeGiftSource(item) === sourceStr &&
        String(itemProperties(item)._gift_parent_variant_id || '') === oldId
      );
    });

    if (!targets.length) return Promise.resolve(false);

    return targets
      .reduce(function (chain, item) {
        return chain.then(function () {
          var props = Object.assign({}, item.properties, { _gift_parent_variant_id: newId });
          return changeLine({ id: item.key, quantity: item.quantity, properties: props });
        });
      }, Promise.resolve())
      .then(function () {
        return true;
      });
  }

  /* The gift properties the new subscription parent needs so the pairing survives the
     upgrade. Mirrors buildParentProperties, but sourced from the gifts already in the
     cart rather than from a rule. */
  function buildReassignedParentProperties(giftItems, source, baseProperties) {
    var variantIds = giftItems.map(function (item) {
      return String(item.variant_id);
    });

    return Object.assign({}, baseProperties || {}, {
      _free_gift_parent: 'true',
      _free_gift_source: source,
      _free_gift_rule: String(itemProperties(giftItems[0])._gift_rule || ''),
      _free_gift_variant_id: variantIds[0],
      _free_gift_variant_ids: variantIds.join(',')
    });
  }

  function findGiftsForParent(cart, source, parentVariantId) {
    return findExistingGiftPair(cart, source, parentVariantId).giftItems;
  }

  window.KNNFreeGift = {
    MANAGED_FREE_GIFT_SOURCES: MANAGED_FREE_GIFT_SOURCES,

    getFreeGiftConfig: getFreeGiftConfig,
    parseFreeGiftConfig: parseFreeGiftConfig,
    isUsableConfig: isUsableConfig,
    getFreeGiftRuleForVariant: getFreeGiftRuleForVariant,
    getFreeGiftsFromRule: getFreeGiftsFromRule,

    isManagedFreeGiftSource: isManagedFreeGiftSource,
    isFreeGiftCartItem: isFreeGiftCartItem,
    isManagedFreeGiftItem: isManagedFreeGiftItem,
    isManagedFreeGiftParentItem: isManagedFreeGiftParentItem,
    isRestrictedGiftProduct: isRestrictedGiftProduct,
    getItemFreeGiftSource: getItemFreeGiftSource,
    buildFreeGiftPairKey: buildFreeGiftPairKey,
    findExistingGiftPair: findExistingGiftPair,
    findGiftsForParent: findGiftsForParent,

    buildOldFunnelSubscriptionGiftItems: buildOldFunnelSubscriptionGiftItems,
    buildOtpGiftItems: buildOtpGiftItems,
    buildReassignedParentProperties: buildReassignedParentProperties,
    dropDuplicateGiftItems: dropDuplicateGiftItems,

    fetchCartJson: fetchCartJson,
    removeCartLinesTogether: removeCartLinesTogether,
    collectLinesForParentRemoval: collectLinesForParentRemoval,
    reassignGiftParents: reassignGiftParents
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initRestrictedGiftProductControls);
  } else {
    initRestrictedGiftProductControls();
  }
})();
